import React from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  TextField,
  MenuItem,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";

export interface FilterConfig {
  label: string;
  field: string;
  options: Array<{ value: string; label: string }>;
  /**
   * Starting value, when "ALL" is not a thing this filter can express.
   *
   * A filter backed by a server query parameter has to start on a real value —
   * there is no request that means "every status at once" — so the default
   * cannot be the implicit "ALL" the rest of them use.
   */
  defaultValue?: string;
}

interface FilterableDataGridProps<T> {
  rows: T[];
  columns: GridColDef[];
  filters?: FilterConfig[];
  searchPlaceholder?: string;
  searchFields?: string[];
  height?: number;
  pageSize?: number;
  /** Fires when a filter dropdown changes — e.g. to trigger a server refetch. */
  onFilterChange?: (field: string, value: string) => void;
  /**
   * Render every row and column rather than only those in view.
   *
   * Only worth setting on a grid whose row count is bounded and small. The
   * reason to want it is that a virtualised grid drops off-screen columns out
   * of the DOM entirely, so anything past the viewport — the action buttons on
   * a wide grid, typically — cannot be asserted on in a test, and a jsdom
   * viewport is narrower than any real one.
   */
  disableVirtualization?: boolean;
  /** Passed through to the underlying DataGrid, for per-screen styling. */
  sx?: SxProps<Theme>;
  /** Per-row class, for styling a row by its data rather than its position. */
  getRowClassName?: (params: { row: T }) => string;
}

export function FilterableDataGrid<T extends { id: string | number }>({
  rows,
  columns,
  filters = [],
  searchPlaceholder = "Search",
  searchFields = [],
  height = 600,
  pageSize = 10,
  onFilterChange,
  disableVirtualization = false,
  sx,
  getRowClassName,
}: FilterableDataGridProps<T>) {
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [filterValues, setFilterValues] = React.useState<
    Record<string, string>
  >(
    filters.reduce(
      (acc, filter) => ({
        ...acc,
        [filter.field]: filter.defaultValue ?? "ALL",
      }),
      {}
    )
  );
  const [currentPaginationModel, setCurrentPaginationModel] = React.useState({
    page: 0,
    pageSize,
  });

  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        searchFields.some((field) => {
          const value = (row as any)[field];
          return (
            value &&
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
          );
        });

      // Apply all custom filters
      const matchesFilters = filters.every((filter) => {
        const filterValue = filterValues[filter.field];
        if (filterValue === "ALL") return true;
        const rowValue = (row as any)[filter.field];
        return String(rowValue).toUpperCase() === filterValue;
      });

      return matchesSearch && matchesFilters;
    });
  }, [rows, searchQuery, filterValues, searchFields, filters]);

  // A `defaultValue` derived from a caller's state would otherwise be read once
  // and never again, leaving the dropdown asserting one thing while the rows
  // say another — and since these filters also narrow client-side, a stale
  // value does not merely mislabel the grid, it empties it.
  const defaultValueKey = filters
    .map((filter) => `${filter.field}:${filter.defaultValue ?? "ALL"}`)
    .join("|");
  React.useEffect(() => {
    setFilterValues((prev) => {
      let changed = false;
      const next = { ...prev };
      filters.forEach((filter) => {
        const incoming = filter.defaultValue ?? "ALL";
        if (filter.defaultValue !== undefined && prev[filter.field] !== incoming) {
          next[filter.field] = incoming;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // Keyed on the values themselves: `filters` is rebuilt inline by every
    // caller, so depending on the array identity would run this every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValueKey]);

  const handleFilterChange = (field: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
    onFilterChange?.(field, value);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {(searchFields.length > 0 || filters.length > 0) && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {searchFields.length > 0 && (
              <TextField
                label="Search"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
            )}
            {filters.map((filter) => (
              <TextField
                key={filter.field}
                select
                label={filter.label}
                value={filterValues[filter.field]}
                onChange={(e) =>
                  handleFilterChange(filter.field, e.target.value)
                }
                size="small"
                sx={{ width: { xs: "100%", sm: 150 } }}
              >
                {filter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </Stack>
        </Stack>
      )}

      <Box sx={{ height, width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          paginationModel={currentPaginationModel}
          onPaginationModelChange={(newPaginationModel) =>
            setCurrentPaginationModel(newPaginationModel)
          }
          pageSizeOptions={[10, 25, 50, 100]}
          disableVirtualization={disableVirtualization}
          sx={sx}
          getRowClassName={getRowClassName}
          disableRowSelectionOnClick
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <strong>
          Showing {filteredRows.length} of {rows.length} items
        </strong>
      </Box>
    </Box>
  );
}
